import React, { useState, useEffect, useRef } from "react";

const Hero = () => {
  const [typedName, setTypedName] = useState("");
  const [typedTitle, setTypedTitle] = useState("");
  const [showCursor, setShowCursor] = useState(true);
  const [heroVisible, setHeroVisible] = useState(false);
  const canvasRef = useRef(null);
  const imageContainerRef = useRef(null);
  const [cursorPosition, setCursorPosition] = useState({ x: -100, y: -100 });
  const [isHoveringImage, setIsHoveringImage] = useState(false);
  const [holeSize, setHoleSize] = useState(0);
  const [rippleTime, setRippleTime] = useState(0);
  const [pulseTime, setPulseTime] = useState(0);

  const fullName = "Ermiyas Bedasa";
  const fullTitle = "Professional Real Estate Advisor";

  useEffect(() => {
    setHeroVisible(true);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (heroVisible) {
        startTypingAnimations();
      }
    }, 1000); 

    return () => clearTimeout(timer);
  }, [heroVisible]);

  const startTypingAnimations = () => {
    let nameIndex = 0;
    const nameSpeed = 80;

    const typeName = () => {
      if (nameIndex <= fullName.length) {
        setTypedName(fullName.slice(0, nameIndex));
        nameIndex++;
        setTimeout(typeName, nameSpeed);
      } else {
        setTimeout(startTitleTyping, 300);
      }
    };

    const startTitleTyping = () => {
      let titleIndex = 0;
      const titleSpeed = 40;

      const typeTitle = () => {
        if (titleIndex <= fullTitle.length) {
          setTypedTitle(fullTitle.slice(0, titleIndex));
          titleIndex++;
          setTimeout(typeTitle, titleSpeed);
        } else {
          setShowCursor(false);
        }
      };
      typeTitle();
    };
    typeName();
  };

  useEffect(() => {
    if (!isHoveringImage) return;
    const interval = setInterval(() => {
      setRippleTime(prev => prev + 0.08);
      setPulseTime(prev => prev + 0.05);
    }, 50);
    return () => clearInterval(interval);
  }, [isHoveringImage]);

  useEffect(() => {
    if (isHoveringImage) {
      const timer = setTimeout(() => setHoleSize(220), 50);
      return () => clearTimeout(timer);
    } else {
      setHoleSize(0);
    }
  }, [isHoveringImage]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !heroVisible) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    const particles = [];

    class Particle {
      constructor() { this.reset(); }
      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2 + 0.5;
        this.speedX = Math.random() * 0.5 - 0.25;
        this.speedY = Math.random() * 0.5 - 0.25;
        this.alpha = Math.random() * 0.3 + 0.1;
        this.color = `rgba(243, 179, 61, ${this.alpha})`;
      }
      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        if (this.x > canvas.width || this.x < 0) this.speedX *= -1;
        if (this.y > canvas.height || this.y < 0) this.speedY *= -1;
      }
      draw() {
        ctx.save();
        ctx.globalAlpha = this.alpha;
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    }

    for (let i = 0; i < 30; i++) { particles.push(new Particle()); }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => { p.update(); p.draw(); });
      animationFrameId = requestAnimationFrame(animate);
    };

    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    animate();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [heroVisible]);

  const handleMouseMove = (e) => {
    if (!imageContainerRef.current) return;
    const rect = imageContainerRef.current.getBoundingClientRect();
    setCursorPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const getHoleMask = () => {
    if (holeSize === 0) return 'none';
    const baseSize = holeSize / 2;
    const spiral1 = Math.sin(rippleTime * 4) * 15;
    const spiral2 = Math.cos(rippleTime * 3 + 2) * 12;
    const pulse1 = Math.sin(pulseTime * 6) * 8;
    const pulse2 = Math.cos(pulseTime * 8 + 1) * 6;
    const wave1 = Math.sin(rippleTime * 5 + cursorPosition.x * 0.01) * 10;
    const wave2 = Math.cos(rippleTime * 7 + cursorPosition.y * 0.01) * 8;
    
    return `
      radial-gradient(
        circle ${baseSize + spiral1}px at ${cursorPosition.x}px ${cursorPosition.y}px,
        transparent 0%,
        transparent ${baseSize - 20 + pulse1}px,
        rgba(0,0,0,0.3) ${baseSize - 10 + wave1}px,
        rgba(0,0,0,0.6) ${baseSize + spiral2}px,
        rgba(0,0,0,0.8) ${baseSize + 10 + pulse2}px,
        black ${baseSize + 25 + wave2}px,
        black 100%
      ),
      radial-gradient(
        circle ${baseSize + 50}px at ${cursorPosition.x}px ${cursorPosition.y}px,
        transparent 0%,
        transparent ${baseSize + 30}px,
        rgba(0,0,0,0.1) ${baseSize + 40}px,
        transparent 100%
      )
    `;
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) element.scrollIntoView({ behavior: 'instant' });
  };

  const handleCall = () => { window.open('tel:+251956445555'); };

  const ArrowDownIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
    </svg>
  );

  const PhoneCallIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
    </svg>
  );

  return (
    <>
      <style>
        {`
          @keyframes slideInLeft {
            0% { opacity: 0; transform: translateX(-100px); }
            100% { opacity: 1; transform: translateX(0); }
          }

          @keyframes slideInRight {
            0% { opacity: 0; transform: translateX(100px); }
            100% { opacity: 1; transform: translateX(0); }
          }

          @keyframes gradientShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }

          .hero {
            height: 90vh;
            min-height: 600px;
            background: linear-gradient(135deg, #0a0f1c 0%, #0c1120 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
            overflow: hidden;
            font-family: 'Cormorant Garamond', serif;
          }

          .particles-canvas {
            position: absolute;
            top: 0; left: 0; width: 100%; height: 100%;
            pointer-events: none;
            opacity: 0.4;
          }

          .hero-container {
            max-width: 1200px;
            width: 100%;
            display: grid;
            grid-template-columns: 1.2fr 0.8fr;
            gap: 40px;
            padding: 0 40px;
            align-items: center;
            position: relative;
            z-index: 2;
          }

          .hero-left {
            margin-top: 40px;
            opacity: 0;
            animation: slideInLeft 1s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
          }

          .hero-right {
            margin-top: 50px;
            opacity: 0;
            animation: slideInRight 1s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
          }

          .name-container {
            margin-bottom: 1.5rem;
          }

          .name-typing {
            font-size: 3.5rem;
            font-weight: 600;
            background: linear-gradient(45deg, #f3b33d, #ff6b6b, #4ecdc4, #45b7d1);
            background-size: 400% 400%;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            line-height: 1.1;
            margin-bottom: 0.5rem;
            min-height: 60px;
            display: inline-block;
          }

          /* NEW: Side by side name display for mobile */
          .side-by-side-name {
            display: none;
          }

          .title-typing {
            font-size: 1.6rem;
            color: #ffffff;
            font-weight: 300;
            letter-spacing: 1px;
            min-height: 40px;
            font-family: 'Comfortaa', cursive;
          }

          .hero-description {
            font-size: 1.1rem;
            line-height: 1.6;
            color: #9aa7b2;
            margin-bottom: 2rem;
            font-family: 'Comfortaa', cursive;
          }

          .button-container {
            display: flex;
            gap: 1rem;
          }

          .transparent-btn, .call-btn {
            padding: 12px 24px;
            font-family: 'Comfortaa', cursive;
            font-size: 0.9rem;
            font-weight: 600;
            border-radius: 10px;
            cursor: pointer;
            transition: all 0.3s ease;
            text-transform: uppercase;
            background: transparent;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .transparent-btn { border: 2px solid rgba(243, 179, 61, 0.3); color: #f3b33d; }
          .call-btn { border: 2px solid rgba(37, 211, 102, 0.4); color: #25D366; }

          .image-stack-container {
            position: relative;
            width: 350px;
            height: 450px;
            border-radius: 15px;
            overflow: hidden;
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);
            cursor: pointer;
          }

          .image-back, .image-front {
            position: absolute;
            top: 0; left: 0; width: 100%; height: 100%;
            background-size: cover;
            background-position: center;
            border-radius: 15px;
          }

          .image-back { background-image: url('/images/hero/buttomimage.webp'); z-index: 1; }
          .image-front { background-image: url('/images/hero/coverimage.webp'); z-index: 2; transition: all 0.3s ease; pointer-events: none; }

          .image-instruction {
            position: absolute;
            bottom: -40px;
            left: 50%;
            transform: translateX(-50%);
            color: #9aa7b2;
            font-size: 0.85rem;
            background: rgba(10, 15, 28, 0.9);
            padding: 6px 15px;
            border-radius: 20px;
          }

          @media (max-width: 968px) {
            .hero-container { 
              display: flex; 
              flex-direction: column-reverse; /* IMAGE ON TOP, TEXT BELOW */
              text-align: center; 
              gap: 20px;
            }
            .hero-left { animation: slideInLeft 0.8s ease forwards; }
            .hero-right { animation: slideInRight 0.8s ease forwards; }
            .hero-left, .hero-right { margin-top: 20px; }
            .button-container { justify-content: center; }
            .image-stack-container { margin: 0 auto; width: 280px; height: 350px; }
          }

          /* NEW: iPhone and small screen specific styles for SIDE BY SIDE name */
          @media (max-width: 768px) {
            /* Hide single-line typing animation on mobile */
            .name-typing {
              display: none;
            }
            
            /* Show side-by-side name layout */
            .side-by-side-name {
              display: flex;
              flex-wrap: nowrap;
              justify-content: center;
              align-items: center;
              gap: 8px;
              font-size: 2.8rem;
              font-weight: 600;
              background: linear-gradient(45deg, #f3b33d, #ff6b6b, #4ecdc4, #45b7d1);
              background-size: 400% 400%;
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
              line-height: 1.1;
              margin-bottom: 0.5rem;
              min-height: 60px;
              width: 100%;
              flex-shrink: 0;
            }
            
            .first-name, .last-name {
              display: inline-block;
              white-space: nowrap;
            }
            
            .title-typing {
              font-size: 1.4rem;
              min-height: 35px;
            }
          }

          /* For very small screens, adjust spacing */
          @media (max-width: 480px) {
            .side-by-side-name {
              font-size: 2.5rem;
              gap: 6px;
              flex-wrap: nowrap;
              justify-content: center;
            }
            
            .first-name, .last-name {
              flex-shrink: 0;
            }
            
            .title-typing {
              font-size: 1.3rem;
            }
          }

          /* For iPhone mini and very narrow screens */
          @media (max-width: 375px) {
            .side-by-side-name {
              font-size: 2.3rem;
              gap: 4px;
            }
            
            .title-typing {
              font-size: 1.2rem;
            }
          }

          /* For extremely small screens */
          @media (max-width: 320px) {
            .side-by-side-name {
              font-size: 2.1rem;
              gap: 3px;
            }
            
            .first-name, .last-name {
              letter-spacing: -0.5px;
            }
          }
        `}
      </style>

      <section className="hero" id="hero">
        <canvas ref={canvasRef} className="particles-canvas" />
        <div className="hero-container">
          <div className="hero-left">
            <div className="name-container">
              {/* Single line name for desktop ONLY */}
              <div className="name-typing">
                {typedName}
                {showCursor && <span className="typing-cursor">|</span>}
              </div>
              
              {/* Side by side name for ALL mobile/small screens */}
              <div className="side-by-side-name">
                <span className="first-name">Ermiyas</span>
                <span className="last-name">Bedasa</span>
              </div>
              
              <div className="title-typing">{typedTitle}</div>
            </div>
            <p className="hero-description">
              Transforming real estate visions into exceptional achievements through 
              strategic advisory and market expertise.
            </p>
            <div className="button-container">
              <button className="transparent-btn" onClick={scrollToContact}><ArrowDownIcon /> Journey</button>
              <button className="call-btn" onClick={handleCall}><PhoneCallIcon /> Call</button>
            </div>
          </div>

          <div className="hero-right">
            <div 
              ref={imageContainerRef}
              className="image-stack-container"
              onMouseMove={handleMouseMove}
              onMouseEnter={() => setIsHoveringImage(true)}
              onMouseLeave={() => setIsHoveringImage(false)}
            >
              <div className="image-back"></div>
              <div 
                className="image-front"
                style={{ maskImage: getHoleMask(), WebkitMaskImage: getHoleMask() }}
              ></div>
            </div>
            <div className="image-instruction">Hover to Reveal</div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Hero;